import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

interface Playlist {
  id: string
  name: string
  description?: string
  is_public: boolean
  created_at: string
  video_count?: number
}

interface Props {
  videoId?: string
  userId: string
  isDark: boolean
}

const PlaylistManager: React.FC<Props> = ({ videoId, userId, isDark }) => {
  const [playlists, setPlaylists] = useState<Playlist[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const C = {
    bg: isDark ? 'rgba(255,255,255,.03)' : '#fff',
    border: isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)',
    text: isDark ? '#fff' : '#0f172a',
    sub: isDark ? '#666' : '#94a3b8',
    inputBg: isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.04)',
  }

  useEffect(() => {
    fetchPlaylists()
  }, [])

  const fetchPlaylists = async () => {
    const { data } = await supabase
      .from('playlists')
      .select('*, playlist_videos(count)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (data) {
      setPlaylists(data.map(p => ({
        ...p,
        video_count: p.playlist_videos?.[0]?.count || 0
      })))
    }
  }

  const createPlaylist = async () => {
    if (!newName.trim()) return
    setLoading(true)
    try {
      const { error } = await supabase.from('playlists').insert({
        user_id: userId,
        name: newName.trim(),
        description: newDesc.trim(),
        is_public: isPublic
      })
      if (error) throw error
      
      setNewName('')
      setNewDesc('')
      setIsPublic(false)
      setShowCreate(false)
      setMessage('Playlist created!')
      setTimeout(() => setMessage(''), 2000)
      fetchPlaylists()
    } catch (e: any) {
      setMessage('Error: ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  const addToPlaylist = async (playlistId: string) => {
    if (!videoId) return
    setLoading(true)
    try {
      const { data: existing } = await supabase
        .from('playlist_videos')
        .select('id')
        .eq('playlist_id', playlistId)
        .eq('video_id', videoId)
        .single()

      if (existing) {
        setMessage('Already in this playlist')
        setTimeout(() => setMessage(''), 2000)
        return
      }

      const { error } = await supabase.from('playlist_videos').insert({
        playlist_id: playlistId,
        video_id: videoId,
        position: 0
      })
      if (error) throw error

      setMessage('Added to playlist!')
      setTimeout(() => setMessage(''), 2000)
      fetchPlaylists()
    } catch (e: any) {
      setMessage('Error: ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ marginTop: 20 }}>
      <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
        📋 Playlists
      </h3>

      {message && (
        <div style={{
          padding: '8px 16px',
          borderRadius: 10,
          background: message.includes('Error') ? 'rgba(229,57,53,.1)' : 'rgba(34,197,94,.1)',
          color: message.includes('Error') ? '#e53935' : '#22c55e',
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 12
        }}>
          {message}
        </div>
      )}

      {!showCreate ? (
        <button
          onClick={() => setShowCreate(true)}
          style={{
            width: '100%', padding: '10px', borderRadius: 10,
            border: `1px dashed ${C.border}`, background: 'transparent',
            color: C.sub, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            marginBottom: 12
          }}
        >
          + Create New Playlist
        </button>
      ) : (
        <div style={{ marginBottom: 12, padding: 16, background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Playlist name"
            style={{
              width: '100%', padding: '8px 12px', marginBottom: 8,
              background: C.inputBg, border: `1px solid ${C.border}`,
              borderRadius: 8, color: C.text, fontSize: 14, outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <textarea
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            style={{
              width: '100%', padding: '8px 12px', marginBottom: 8,
              background: C.inputBg, border: `1px solid ${C.border}`,
              borderRadius: 8, color: C.text, fontSize: 13, outline: 'none',
              resize: 'none', boxSizing: 'border-box'
            }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={e => setIsPublic(e.target.checked)}
              style={{ accentColor: '#e53935' }}
            />
            <span style={{ color: C.sub, fontSize: 12 }}>Public playlist</span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowCreate(false)}
              style={{
                flex: 1, padding: '8px', borderRadius: 8,
                border: `1px solid ${C.border}`, background: 'transparent',
                color: C.sub, fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={createPlaylist}
              disabled={loading || !newName.trim()}
              style={{
                flex: 2, padding: '8px', borderRadius: 8,
                border: 'none', background: loading ? 'rgba(229,57,53,.5)' : 'linear-gradient(90deg,#e53935,#8e24aa)',
                color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {playlists.map(playlist => (
          <button
            key={playlist.id}
            onClick={() => videoId ? addToPlaylist(playlist.id) : null}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 14px', borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: 'transparent', cursor: 'pointer',
              textAlign: 'left', width: '100%',
              transition: 'all .15s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.03)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
            }}
          >
            <div>
              <p style={{ color: C.text, fontSize: 13, fontWeight: 600, margin: 0 }}>
                {playlist.name}
              </p>
              <p style={{ color: C.sub, fontSize: 11, margin: '2px 0 0' }}>
                {playlist.video_count || 0} videos · {playlist.is_public ? 'Public' : 'Private'}
              </p>
            </div>
            {videoId && <span style={{ color: '#e53935', fontSize: 18 }}>+</span>}
          </button>
        ))}
      </div>
    </div>
  )
}

export default PlaylistManager